import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type State = 'ok' | 'due_soon' | 'overdue' | 'never' | 'out_of_service' | 'retired';

interface InstrumentRow {
  id: string;
  code: string;
  name: string;
  instrumentType: string | null;
  make: string | null;
  serialNumber: string | null;
  range: string | null;
  leastCount: string | null;
  location: string | null;
  custodian: string | null;
  calibrationFrequencyDays: number;
  lastCalibratedOn: string | null;
  nextDueOn: string | null;
  status: 'active' | 'out_of_service' | 'retired';
  statusNote: string | null;
  notes: string | null;
  state: State;
  daysLeft: number | null;
}
interface CalibrationRecord {
  id: string;
  calibrationNumber: string;
  calibratedOn: string;
  agency: string;
  certificateNumber: string | null;
  standardUsed: string | null;
  permissibleError: number | null;
  readings: { nominal: number; observed: number }[] | null;
  result: 'pass' | 'fail';
  nextDueOn: string | null;
  remarks: string | null;
  recordedBy: string | null;
}
interface Instrument extends InstrumentRow {
  records: CalibrationRecord[];
  usedIn: { id: string; lotNumber: string; productName: string; inspectedAt: string | null; checks: number; sinceLastCalibration: boolean }[];
  inspectionsToReview?: number;
}
interface InstrumentPage {
  data: InstrumentRow[];
  total: number;
  totalPages: number;
}
interface Summary {
  inUse: number;
  dueSoon: number;
  overdue: number;
  neverCalibrated: number;
  outOfService: number;
  retired: number;
  dueSoonDays: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATE: Record<State, { label: string; className: string }> = {
  ok: { label: 'In calibration', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  due_soon: { label: 'Due soon', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  overdue: { label: 'Overdue', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  never: { label: 'Not calibrated', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  out_of_service: { label: 'Out of service', className: 'bg-gray-100 text-gray-700 ring-gray-500/30' },
  retired: { label: 'Retired', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
const dueText = (row: InstrumentRow) => {
  if (row.daysLeft === null) return 'No calibration on record';
  if (row.daysLeft < 0) return `${formatNumber(-row.daysLeft)} day(s) overdue`;
  if (row.daysLeft === 0) return 'Due today';
  return `${formatNumber(row.daysLeft)} day(s) left`;
};

const instrumentApi = {
  page: async (params: object): Promise<InstrumentPage> => (await api.get('/instruments', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/instruments/summary')).data,
  one: async (id: string): Promise<Instrument> => (await api.get(`/instruments/${id}`)).data,
  save: async (id: string | null, data: object): Promise<Instrument> => (id ? await api.put(`/instruments/${id}`, data) : await api.post('/instruments', data)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/instruments/${id}`);
  },
  calibrate: async (id: string, data: object): Promise<Instrument> => (await api.post(`/instruments/${id}/calibrations`, data)).data,
  removeCalibration: async (id: string, recordId: string): Promise<Instrument> => (await api.delete(`/instruments/${id}/calibrations/${recordId}`)).data,
  setStatus: async (id: string, status: string, reason: string | null): Promise<Instrument> => (await api.post(`/instruments/${id}/status`, { status, reason })).data,
};

// ---------------------------------------------------------------- add or change an instrument

const InstrumentForm: React.FC<{ instrument: Instrument | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ instrument, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({
    code: instrument?.code ?? '',
    name: instrument?.name ?? '',
    instrumentType: instrument?.instrumentType ?? '',
    make: instrument?.make ?? '',
    serialNumber: instrument?.serialNumber ?? '',
    range: instrument?.range ?? '',
    leastCount: instrument?.leastCount ?? '',
    location: instrument?.location ?? '',
    custodian: instrument?.custodian ?? '',
    calibrationFrequencyDays: String(instrument?.calibrationFrequencyDays ?? 365),
    notes: instrument?.notes ?? '',
    lastCalibratedOn: '',
    nextDueOn: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: field === 'code' ? event.target.value.toUpperCase() : event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const frequency = Number(form.calibrationFrequencyDays);
    if (!/^[A-Z0-9][A-Z0-9/-]{1,29}$/.test(form.code.trim())) next.code = '2 to 30 capitals, digits, hyphens or slashes';
    if (form.name.trim().length < 2) next.name = 'Name the instrument';
    if (!Number.isInteger(frequency) || frequency < 1 || frequency > 3650) next.calibrationFrequencyDays = 'A whole number of days, 1 to 3650';
    if (!instrument) {
      if (form.lastCalibratedOn && form.lastCalibratedOn > today()) next.lastCalibratedOn = 'Not in the future';
      if (form.nextDueOn && !form.lastCalibratedOn) next.lastCalibratedOn = 'Enter when it was calibrated';
      if (form.nextDueOn && form.lastCalibratedOn && form.nextDueOn <= form.lastCalibratedOn) next.nextDueOn = 'After the calibration date';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const { lastCalibratedOn, nextDueOn, calibrationFrequencyDays: _frequency, ...rest } = form;
    onSubmit({ ...rest, code: form.code.trim(), name: form.name.trim(), calibrationFrequencyDays: frequency, ...(instrument ? {} : { lastCalibratedOn: lastCalibratedOn || null, nextDueOn: nextDueOn || null }) });
  };
  const input = (field: keyof typeof form, label: string, extra: { required?: boolean; placeholder?: string; maxLength?: number; className?: string; type?: string; hint?: string } = {}) => (
    <Field label={label} htmlFor={`in-${field}`} required={extra.required} error={errors[field]} hint={extra.hint} className={extra.className}>
      <input id={`in-${field}`} type={extra.type ?? 'text'} maxLength={extra.maxLength} placeholder={extra.placeholder} value={form[field]} onChange={set(field)} className={cn(inputClass, errors[field] && inputErrorClass)} />
    </Field>
  );

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {input('code', 'Code', { required: true, placeholder: 'VC-014', maxLength: 30, hint: 'The number tagged on it' })}
        {input('name', 'Name', { required: true, placeholder: 'Digital vernier caliper', maxLength: 100, className: 'sm:col-span-2' })}
        {input('instrumentType', 'Type', { placeholder: 'Vernier caliper', maxLength: 60 })}
        {input('make', 'Make', { maxLength: 60 })}
        {input('serialNumber', 'Serial number', { maxLength: 60 })}
        {input('range', 'Range', { placeholder: '0–150 mm', maxLength: 60 })}
        {input('leastCount', 'Least count', { placeholder: '0.01 mm', maxLength: 30 })}
        {input('calibrationFrequencyDays', 'Calibrate every (days)', { required: true, type: 'number' })}
        {input('location', 'Kept at', { maxLength: 100 })}
        {input('custodian', 'Issued to', { maxLength: 100 })}
        {input('notes', 'Notes', { maxLength: 500 })}
      </div>
      {!instrument && (
        <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-3 ring-1 ring-gray-200 sm:grid-cols-3">
          <p className="text-sm text-gray-700 sm:col-span-3">If the instrument already carries a calibration, enter it here. Without one it cannot be used for inspection until a calibration is recorded.</p>
          {input('lastCalibratedOn', 'Last calibrated on', { type: 'date' })}
          {input('nextDueOn', 'Calibration runs out on', { type: 'date', hint: 'Left empty, the interval after the last calibration' })}
        </div>
      )}
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : instrument ? 'Save Instrument' : 'Add Instrument'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- record a calibration

const CalibrationForm: React.FC<{ instrument: Instrument; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ instrument, isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ calibratedOn: today(), agency: '', certificateNumber: '', standardUsed: '', permissibleError: '', result: '' as '' | 'pass' | 'fail', nextDueOn: '', remarks: '' });
  const [readings, setReadings] = useState<{ nominal: string; observed: string }[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const filled = readings.filter((reading) => reading.nominal.trim() !== '' || reading.observed.trim() !== '');
  const allowed = form.permissibleError.trim() === '' ? null : Number(form.permissibleError);
  // With readings and a permissible error the result follows from them
  const worst = filled.length > 0 && filled.every((reading) => Number.isFinite(Number(reading.nominal)) && Number.isFinite(Number(reading.observed)) && reading.nominal.trim() !== '' && reading.observed.trim() !== '') ? Math.max(...filled.map((reading) => Math.abs(Number(reading.observed) - Number(reading.nominal)))) : null;
  const judged: 'pass' | 'fail' | null = worst !== null && allowed !== null && Number.isFinite(allowed) ? (worst <= allowed + 1e-9 ? 'pass' : 'fail') : null;
  const result = judged ?? (form.result || null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.calibratedOn) next.calibratedOn = 'Enter the date';
    else if (form.calibratedOn > today()) next.calibratedOn = 'Not in the future';
    if (form.agency.trim().length < 2) next.agency = 'Say who calibrated it';
    if (allowed !== null && (!Number.isFinite(allowed) || allowed < 0)) next.permissibleError = 'Enter a number';
    if (filled.some((reading) => reading.nominal.trim() === '' || reading.observed.trim() === '' || !Number.isFinite(Number(reading.nominal)) || !Number.isFinite(Number(reading.observed)))) next.readings = 'Each reading needs both values';
    if (!result) next.result = 'Say whether it passed';
    if (result === 'pass' && form.nextDueOn && form.calibratedOn && form.nextDueOn <= form.calibratedOn) next.nextDueOn = 'After the calibration date';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      calibratedOn: form.calibratedOn,
      agency: form.agency.trim(),
      certificateNumber: form.certificateNumber.trim() || null,
      standardUsed: form.standardUsed.trim() || null,
      permissibleError: allowed,
      readings: filled.map((reading) => ({ nominal: Number(reading.nominal), observed: Number(reading.observed) })),
      result,
      nextDueOn: result === 'pass' ? form.nextDueOn || null : null,
      remarks: form.remarks.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Calibrated on" htmlFor="cal-date" required error={errors.calibratedOn}>
          <input id="cal-date" type="date" max={today()} value={form.calibratedOn} onChange={set('calibratedOn')} className={cn(inputClass, errors.calibratedOn && inputErrorClass)} />
        </Field>
        <Field label="Calibrated by" htmlFor="cal-agency" required error={errors.agency} hint="The laboratory, or In-house">
          <input id="cal-agency" type="text" maxLength={120} value={form.agency} onChange={set('agency')} className={cn(inputClass, errors.agency && inputErrorClass)} />
        </Field>
        <Field label="Certificate number" htmlFor="cal-certificate">
          <input id="cal-certificate" type="text" maxLength={80} value={form.certificateNumber} onChange={set('certificateNumber')} className={inputClass} />
        </Field>
        <Field label="Master it was compared with" htmlFor="cal-standard">
          <input id="cal-standard" type="text" maxLength={200} value={form.standardUsed} onChange={set('standardUsed')} className={inputClass} />
        </Field>
      </div>

      <section className="rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Readings</h4>
          <label htmlFor="cal-error" className="flex items-center gap-2 text-sm text-gray-700">
            Permissible error
            <input id="cal-error" type="number" min="0" step="0.00001" value={form.permissibleError} onChange={set('permissibleError')} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors.permissibleError && inputErrorClass)} />
          </label>
          {worst !== null && <span className="text-xs text-gray-500">Largest error {formatNumber(worst, 5)}</span>}
        </div>
        {readings.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {readings.map((reading, index) => {
              const error = reading.nominal.trim() !== '' && reading.observed.trim() !== '' ? Number(reading.observed) - Number(reading.nominal) : null;
              const set2 = (field: 'nominal' | 'observed') => (event: React.ChangeEvent<HTMLInputElement>) => {
                setReadings((prev) => prev.map((row, position) => (position === index ? { ...row, [field]: event.target.value } : row)));
                setErrors({});
              };
              return (
                <li key={index} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm text-gray-700">
                  <span className="w-6 text-gray-500">{index + 1}</span>
                  <input type="number" step="0.00001" value={reading.nominal} onChange={set2('nominal')} placeholder="Master" aria-label={`Master value of reading ${index + 1}`} className={cn(inputClass, 'w-32 py-1 text-right tabular-nums')} />
                  <input type="number" step="0.00001" value={reading.observed} onChange={set2('observed')} placeholder="Instrument" aria-label={`Instrument value of reading ${index + 1}`} className={cn(inputClass, 'w-32 py-1 text-right tabular-nums')} />
                  <span className={cn('w-28 tabular-nums', error !== null && allowed !== null && Math.abs(error) > allowed + 1e-9 ? 'font-medium text-red-600' : 'text-gray-500')}>{error === null || !Number.isFinite(error) ? '' : `Error ${error > 0 ? '+' : ''}${formatNumber(error, 5)}`}</span>
                  <button type="button" onClick={() => setReadings((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove reading ${index + 1}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex items-center gap-3 border-t border-gray-200 p-3">
          <button type="button" onClick={() => setReadings((prev) => [...prev, { nominal: '', observed: '' }])} className={secondaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            Add Reading
          </button>
          <span className="text-xs text-gray-500">Optional. With readings and a permissible error, the result follows from them.</span>
          {errors.readings && <span className="text-sm text-red-600">{errors.readings}</span>}
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Result" htmlFor="cal-result" required error={errors.result}>
          {judged ? (
            <p id="cal-result" className={cn('rounded-md px-3 py-2 text-sm font-semibold ring-1', judged === 'pass' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{judged === 'pass' ? 'Pass, by the readings' : 'Fail, by the readings'}</p>
          ) : (
            <select id="cal-result" value={form.result} onChange={set('result')} className={cn(inputClass, errors.result && inputErrorClass)}>
              <option value="">Choose…</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
            </select>
          )}
        </Field>
        {result === 'fail' ? (
          <p className="self-end rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-600/20">{instrument.code} will be taken out of service until it passes a calibration.</p>
        ) : (
          <Field label="Next due on" htmlFor="cal-due" error={errors.nextDueOn} hint={`Left empty, ${formatNumber(instrument.calibrationFrequencyDays)} days after the calibration`}>
            <input id="cal-due" type="date" min={form.calibratedOn || undefined} value={form.nextDueOn} onChange={set('nextDueOn')} className={cn(inputClass, errors.nextDueOn && inputErrorClass)} />
          </Field>
        )}
        <Field label="Remarks" htmlFor="cal-remarks" className="sm:col-span-2">
          <input id="cal-remarks" type="text" maxLength={500} value={form.remarks} onChange={set('remarks')} className={inputClass} />
        </Field>
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Back
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Record Calibration'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one instrument

const InstrumentPanel: React.FC<{ instrumentId: string; canCreate: boolean; canEdit: boolean; canDelete: boolean; onEdit: (instrument: Instrument) => void; onClose: () => void; onChanged: () => void }> = ({ instrumentId, canCreate, canEdit, canDelete, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: instrument, isLoading, isError, error } = useQuery({ queryKey: ['instruments', 'one', instrumentId], queryFn: () => instrumentApi.one(instrumentId) });
  const [calibrating, setCalibrating] = useState(false);
  const [asking, setAsking] = useState<'out_of_service' | 'retired' | null>(null);
  const [reason, setReason] = useState('');
  const store = (saved: Instrument) => {
    queryClient.setQueryData(['instruments', 'one', instrumentId], saved);
    queryClient.invalidateQueries({ queryKey: ['quality', 'usable-instruments'] });
    onChanged();
  };
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 });

  const calibrate = useMutation({
    mutationFn: (input: object) => instrumentApi.calibrate(instrumentId, input),
    onSuccess: (saved) => {
      const record = saved.records[0];
      if (record.result === 'pass') toast.success(`${record.calibrationNumber}: ${saved.code} passed, next due ${formatDate(saved.nextDueOn)}`);
      else toast.error(`${record.calibrationNumber}: ${saved.code} failed and is out of service.${saved.inspectionsToReview ? ` ${saved.inspectionsToReview} inspection(s) measured with it since it was last good should be looked at again.` : ''}`, { duration: 10000 });
      store(saved);
      setCalibrating(false);
    },
    onError: fail,
  });
  const takeBack = useMutation({
    mutationFn: (recordId: string) => instrumentApi.removeCalibration(instrumentId, recordId),
    onSuccess: (saved) => {
      toast.success('Calibration record removed');
      store(saved);
    },
    onError: fail,
  });
  const setStatus = useMutation({
    mutationFn: ({ status, note }: { status: string; note: string | null }) => instrumentApi.setStatus(instrumentId, status, note),
    onSuccess: (saved) => {
      toast.success(`${saved.code} is ${saved.status === 'active' ? 'back in use' : saved.status === 'retired' ? 'retired' : 'out of service'}`);
      store(saved);
      setAsking(null);
      setReason('');
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => instrumentApi.remove(instrumentId),
    onSuccess: () => {
      toast.success('Instrument deleted');
      onChanged();
      onClose();
    },
    onError: fail,
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !instrument) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The instrument could not be loaded.')}</p>;
  if (calibrating) return <CalibrationForm instrument={instrument} isPending={calibrate.isPending} onCancel={() => setCalibrating(false)} onSubmit={(input) => calibrate.mutate(input)} />;
  const toReview = instrument.usedIn.filter((lot) => lot.sinceLastCalibration);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATE[instrument.state].className)}>{STATE[instrument.state].label}</span>
        <span className="font-semibold text-gray-900">{instrument.name}</span>
        {[instrument.instrumentType, instrument.make, instrument.serialNumber && `S/N ${instrument.serialNumber}`, instrument.range, instrument.leastCount && `least count ${instrument.leastCount}`].filter(Boolean).map((part) => (<span key={String(part)}>{part}</span>))}
        {instrument.location && <span>Kept at {instrument.location}</span>}
        {instrument.custodian && <span>Issued to {instrument.custodian}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-4">
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Last calibrated</p><p className="font-medium text-gray-900">{formatDate(instrument.lastCalibratedOn)}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Next due</p><p className={cn('font-medium', instrument.state === 'overdue' ? 'text-red-600' : 'text-gray-900')}>{formatDate(instrument.nextDueOn)}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Standing</p><p className={cn('font-medium', instrument.state === 'overdue' || instrument.state === 'never' ? 'text-red-600' : 'text-gray-900')}>{instrument.status === 'active' ? dueText(instrument) : STATE[instrument.state].label}</p></div>
        <div><p className="text-xs uppercase tracking-wide text-gray-500">Interval</p><p className="font-medium text-gray-900">{formatNumber(instrument.calibrationFrequencyDays)} days</p></div>
        {instrument.statusNote && <p className="col-span-2 text-gray-700 sm:col-span-4">{instrument.statusNote}</p>}
      </div>
      {(instrument.state === 'overdue' || instrument.state === 'never' || instrument.state === 'out_of_service') && (
        <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-600/20">This instrument cannot be chosen on an inspection until a calibration it passes is recorded.</p>
      )}

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Calibration records · {instrument.records.length}</h4>
        {instrument.records.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No calibration has been recorded.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Record</th>
                  <th scope="col" className={thClass}>Calibrated by</th>
                  <th scope="col" className={thClass}>Found</th>
                  <th scope="col" className={thClass}>Result</th>
                  <th scope="col" className={thClass}>Next due</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {instrument.records.map((record, index) => (
                  <tr key={record.id}>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-900">
                      {record.calibrationNumber}
                      <span className="block text-xs text-gray-500">{formatDate(record.calibratedOn)}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {record.agency}
                      <span className="block text-xs text-gray-500">{[record.certificateNumber && `Certificate ${record.certificateNumber}`, record.standardUsed && `against ${record.standardUsed}`].filter(Boolean).join(' · ')}</span>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {record.readings && record.readings.length > 0 ? (
                        <>
                          {record.readings.length} reading(s), largest error {formatNumber(Math.max(...record.readings.map((reading) => Math.abs(reading.observed - reading.nominal))), 5)}
                          {record.permissibleError !== null && <span className="block text-xs text-gray-500">{formatNumber(record.permissibleError, 5)} allowed</span>}
                        </>
                      ) : (
                        <span className="text-gray-500">{record.remarks || '—'}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2"><span className={cn(badgeClass, record.result === 'pass' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>{record.result === 'pass' ? 'Pass' : 'Fail'}</span></td>
                    <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">{formatDate(record.nextDueOn)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {index === 0 && canEdit && (
                        <button type="button" onClick={() => takeBack.mutate(record.id)} disabled={takeBack.isPending} title="Remove this record, entered by mistake" aria-label={`Remove ${record.calibrationNumber}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {instrument.usedIn.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Inspections it was used for · {toReview.length} since it was last calibrated</h4>
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {instrument.usedIn.map((lot) => (
              <li key={lot.id} className="flex flex-wrap items-baseline gap-x-4 px-4 py-2">
                <Link to="/inspections" className="font-medium text-indigo-600 hover:text-indigo-500">{lot.lotNumber}</Link>
                <span className="min-w-0 flex-1 text-gray-700">{lot.productName}</span>
                <span className="text-xs text-gray-500">{formatDate(lot.inspectedAt)} · {lot.checks} check(s){lot.sinceLastCalibration ? '' : ' · before the last calibration'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {asking ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label={asking === 'retired' ? 'Why is it retired?' : 'Why is it out of service?'} htmlFor="in-reason" required>
            <input id="in-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => { setAsking(null); setReason(''); }} className={secondaryButtonClass}>Back</button>
            <button type="button" disabled={setStatus.isPending || reason.trim().length < 3} onClick={() => setStatus.mutate({ status: asking, note: reason.trim() })} className={dangerButtonClass}>
              {asking === 'retired' ? 'Confirm Retiring' : 'Confirm'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {canDelete && instrument.records.length === 0 && instrument.usedIn.length === 0 && (
            <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>
          )}
          {canEdit && (
            <>
              <button type="button" onClick={() => onEdit(instrument)} className={secondaryButtonClass}>Edit</button>
              {instrument.status !== 'retired' && <button type="button" onClick={() => setAsking('retired')} className={secondaryButtonClass}>Retire</button>}
              {instrument.status === 'active' && <button type="button" onClick={() => setAsking('out_of_service')} className={secondaryButtonClass}>Take Out of Service</button>}
              {instrument.status !== 'active' && <button type="button" onClick={() => setStatus.mutate({ status: 'active', note: null })} disabled={setStatus.isPending} className={secondaryButtonClass}>Bring Back into Use</button>}
            </>
          )}
          {canCreate && instrument.status !== 'retired' && (
            <button type="button" onClick={() => setCalibrating(true)} className={primaryButtonClass}>Record Calibration</button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Instruments: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('quality.create');
  const canEdit = can('quality.update');
  const canDelete = can('quality.delete');
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<State | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Instrument | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['instruments', 'page', { page, limit, search, state: stateFilter }],
    queryFn: () => instrumentApi.page({ page, limit, search, state: stateFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['instruments', 'summary'], queryFn: instrumentApi.summary });
  const { data: viewing } = useQuery({ queryKey: ['instruments', 'one', viewingId], queryFn: () => instrumentApi.one(viewingId as string), enabled: !!viewingId });

  const refreshLists = () => ['page', 'summary'].forEach((key) => queryClient.invalidateQueries({ queryKey: ['instruments', key] }));
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => instrumentApi.save(editing?.id ?? null, input),
    onSuccess: (instrument) => {
      toast.success(editing ? `${instrument.code} saved` : `${instrument.code} added`);
      queryClient.setQueryData(['instruments', 'one', instrument.id], instrument);
      refreshLists();
      closeForm();
      setViewingId(instrument.id);
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The instrument could not be saved')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!stateFilter;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const blocked = (summary?.overdue ?? 0) + (summary?.neverCalibrated ?? 0);
  const stats = [
    { label: 'In calibration', value: formatNumber(summary?.inUse ?? 0), hint: 'May be used for inspection' },
    { label: `Due within ${summary?.dueSoonDays ?? 30} days`, value: formatNumber(summary?.dueSoon ?? 0), hint: 'Arrange their calibration' },
    { label: 'Overdue or not calibrated', value: formatNumber(blocked), hint: 'Cannot be used for inspection', alert: blocked > 0 },
    { label: 'Out of service', value: formatNumber(summary?.outOfService ?? 0), hint: `${formatNumber(summary?.retired ?? 0)} retired` },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Instruments &amp; Calibration</h1>
          <p className="mt-1 text-sm text-gray-500">The measuring instruments and gauges of the plant, when each is due for calibration and what was found. An instrument past its due date cannot be used on an inspection.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Instrument
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by code, name, type, make, serial number, location or custodian" aria-label="Search instruments" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={stateFilter} onChange={(event) => { setStateFilter(event.target.value as State | ''); setPage(1); }} aria-label="State" className={cn(inputClass, 'lg:w-48')}>
            <option value="">All</option>
            {(Object.keys(STATE) as State[]).map((state) => (<option key={state} value={state}>{STATE[state].label}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Instrument</th>
                <th scope="col" className={thClass}>Range</th>
                <th scope="col" className={thClass}>Kept at</th>
                <th scope="col" className={thClass}>Last calibrated</th>
                <th scope="col" className={thClass}>Next due</th>
                <th scope="col" className={thClass}>State</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The instruments could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No instrument matches' : 'No instruments yet'}</p>
                    <p className="mt-1 text-sm text-gray-500">Add the instruments and gauges that are calibrated at intervals.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className={cn('hover:bg-gray-50', row.status === 'retired' && 'bg-gray-50/60')}>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.code}</button>
                      <p className="mt-0.5 text-xs text-gray-500">{[row.name, row.make].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.range || '—'}{row.leastCount && <span className="block text-xs text-gray-500">least count {row.leastCount}</span>}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.location || '—'}{row.custodian && <span className="block text-xs text-gray-500">{row.custodian}</span>}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatDate(row.lastCalibratedOn)}</td>
                    <td className={cn('whitespace-nowrap px-4 py-3 text-sm', row.state === 'overdue' ? 'font-medium text-red-600' : 'text-gray-700')}>
                      {formatDate(row.nextDueOn)}
                      {row.status === 'active' && <span className={cn('block text-xs', row.state === 'overdue' || row.state === 'never' ? 'text-red-600' : row.state === 'due_soon' ? 'text-amber-700' : 'font-normal text-gray-500')}>{dueText(row)}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATE[row.state].className)}>{STATE[row.state].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(row.id)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={`Open ${row.code}`}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.code}` : 'Add Instrument') : viewing?.code ?? 'Instrument'} size="xl">
        {formOpen ? (
          <InstrumentForm key={editing?.id ?? 'new'} instrument={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && (
            <InstrumentPanel
              key={viewingId}
              instrumentId={viewingId}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={(instrument) => {
                setEditing(instrument);
                setFormOpen(true);
              }}
              onClose={() => setViewingId(null)}
              onChanged={refreshLists}
            />
          )
        )}
      </Modal>
    </div>
  );
};

export default Instruments;
